import { ReactNode, useEffect, useRef } from 'react';
import type { MqttClient } from 'mqtt';
import { useLocation } from 'react-router-dom';
import { MqqtOptions } from '@/shared/const/mqtt';
import { useGlobalContext } from '@/shared/contex/contex';

export function ConnectMqttProvider({ children, handleConnect }: { children?: ReactNode; handleConnect?: () => void }) {
    const { dispatch } = useGlobalContext();
    const { pathname } = useLocation();
    const enabled = /^\/(admin|camera|loco|station|panel)(\/|$)/.test(pathname);
    const onConnect = useRef(handleConnect);
    onConnect.current = handleConnect;
    useEffect(() => {
        if (!enabled) return;
        let disposed = false;
        let suspended = false;
        let generation = 0;
        let client: MqttClient | undefined;
        const connected = () => {
            dispatch({ type: 'changeStatus', payload: 'ready' });
            onConnect.current?.();
        };
        const reconnecting = () => dispatch({ type: 'changeStatus', payload: 'reconnecting' });
        const failed = () => dispatch({ type: 'changeStatus', payload: 'error' });
        async function start() {
            const current = ++generation;
            dispatch({ type: 'changeStatus', payload: 'connecting' });
            try {
                const { default: mqtt } = await import('mqtt');
                if (disposed || suspended || current !== generation) return;
                client = mqtt.connect({ ...MqqtOptions, queueQoSZero: false, resubscribe: false });
                client.on('connect', connected);
                client.on('reconnect', reconnecting);
                client.on('close', reconnecting);
                client.on('error', failed);
                dispatch({ type: 'createClient', payload: client });
            } catch { if (!disposed && !suspended && current === generation) failed(); }
        }
        function stop() {
            generation++;
            if (client) {
                client.removeListener('connect', connected);
                client.removeListener('reconnect', reconnecting);
                client.removeListener('close', reconnecting);
                client.removeListener('error', failed);
                client.end(true);
                client = undefined;
            }
            dispatch({ type: 'createClient', payload: undefined });
        }
        const hide = () => { suspended = true; stop(); };
        const show = (event: PageTransitionEvent) => {
            if (event.persisted) { suspended = false; void start(); }
        };
        window.addEventListener('pagehide', hide);
        window.addEventListener('pageshow', show);
        void start();
        return () => {
            disposed = true;
            window.removeEventListener('pagehide', hide);
            window.removeEventListener('pageshow', show);
            stop();
        };
    }, [dispatch, enabled]);
    return <>{children}</>;
}
